import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Castle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusBadgeColors = {
  reservado: 'bg-blue-100 text-blue-800',
  liquidado: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800',
  completado: 'bg-purple-100 text-purple-800',
};

export default function CalendarioPage() {
  const { getAuthHeader } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [rentas, setRentas] = useState([]);
  const [inflables, setInflables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayRentas, setDayRentas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const mes = currentDate.getMonth() + 1;
      const anio = currentDate.getFullYear();
      const [rentasRes, inflablesRes] = await Promise.all([
        axios.get(`${API_URL}/rentas/calendario?mes=${mes}&anio=${anio}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/inflables`),
      ]);
      setRentas(rentasRes.data);
      setInflables(inflablesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay();
  const paddedDays = [...Array(startDay).fill(null), ...days];

  const getRentasForDay = (day) => {
    if (!day) return [];
    const dateStr = format(day, 'yyyy-MM-dd');
    return rentas.filter(r => r.fecha === dateStr);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const dayRentasList = getRentasForDay(day);
    if (dayRentasList.length > 0) {
      setSelectedDay(day);
      setDayRentas(dayRentasList);
      setDialogOpen(true);
    }
  };

  const getInflableColor = (id) => inflables.find(i => i.id === id)?.color || '#9CA3AF';
  const getInflableName = (id) => inflables.find(i => i.id === id)?.nombre || 'Inflable';

  const getRentaInflableNames = (renta) => {
    if (renta.detalles?.length > 0) return renta.detalles.map(d => d.inflable_nombre || getInflableName(d.inflable_id)).join(', ');
    if (renta.inflable_id) return getInflableName(renta.inflable_id);
    return 'Inflable';
  };

  const getRentaColor = (renta) => {
    if (renta.detalles?.length > 0) return getInflableColor(renta.detalles[0].inflable_id);
    if (renta.inflable_id) return getInflableColor(renta.inflable_id);
    return '#9CA3AF';
  };

  const weekDaysFull = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
  const weekDaysShort = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="calendario-page">
      <div>
        <h1 className="font-heading text-xl sm:text-3xl font-bold text-allegra-dark">Calendario</h1>
        <p className="text-xs sm:text-sm text-allegra-gray">Vista de reservas por mes</p>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2 sm:pb-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="h-8 w-8 p-0" data-testid="prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h2 className="font-heading text-base sm:text-xl font-bold text-allegra-dark capitalize">
              {format(currentDate, 'MMMM yyyy', { locale: es })}
            </h2>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="h-8 w-8 p-0" data-testid="next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-2 sm:px-6 pb-4">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div></div>
          ) : (
            <>
              {/* Week days header */}
              <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 sm:mb-2">
                {weekDaysFull.map((day, i) => (
                  <div key={day} className="text-center text-[10px] sm:text-sm font-medium text-allegra-gray py-1">
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{weekDaysShort[i]}</span>
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                {paddedDays.map((day, index) => {
                  const dayRentasList = day ? getRentasForDay(day) : [];
                  const isToday = day && isSameDay(day, new Date());
                  
                  return (
                    <div
                      key={index}
                      className={`
                        min-h-[48px] sm:min-h-[100px] p-1 sm:p-2 rounded-lg sm:rounded-xl border transition-all
                        ${day ? 'cursor-pointer hover:border-primary hover:bg-primary/5' : 'bg-transparent border-transparent'}
                        ${isToday ? 'border-primary bg-primary/5' : 'border-border/50 sm:border-border'}
                      `}
                      onClick={() => handleDayClick(day)}
                      data-testid={day ? `calendar-day-${format(day, 'yyyy-MM-dd')}` : undefined}
                    >
                      {day && (
                        <>
                          <span className={`text-[10px] sm:text-sm font-medium ${isToday ? 'text-primary' : 'text-allegra-dark'}`}>
                            {format(day, 'd')}
                          </span>
                          {/* Mobile: dots only */}
                          <div className="sm:hidden flex gap-0.5 mt-0.5 flex-wrap">
                            {dayRentasList.slice(0, 4).map((renta) => (
                              <div key={renta.id} className="w-2 h-2 rounded-full" style={{ backgroundColor: getRentaColor(renta) }} />
                            ))}
                          </div>
                          {/* Desktop: full labels */}
                          <div className="hidden sm:block mt-1 space-y-1">
                            {dayRentasList.slice(0, 3).map((renta) => (
                              <div key={renta.id} className="text-xs px-1.5 py-0.5 rounded truncate text-white"
                                style={{ backgroundColor: getRentaColor(renta) }}
                                title={getRentaInflableNames(renta)}>
                                {renta.hora_inicio} - {getRentaInflableNames(renta).substring(0, 12)}
                              </div>
                            ))}
                            {dayRentasList.length > 3 && <div className="text-xs text-allegra-gray text-center">+{dayRentasList.length - 3} mas</div>}
                          </div>
                          {/* Mobile: count badge */}
                          {dayRentasList.length > 4 && (
                            <span className="sm:hidden text-[8px] text-allegra-gray">+{dayRentasList.length - 4}</span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend - compact on mobile */}
              <div className="flex flex-wrap gap-2 sm:gap-4 mt-4 pt-3 border-t">
                {Object.entries(statusBadgeColors).map(([status, cls]) => (
                  <div key={status} className="flex items-center gap-1.5">
                    <Badge className={`text-[9px] sm:text-xs h-4 sm:h-5 ${cls}`}>{status}</Badge>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
              <CalendarIcon className="w-4 h-4 text-primary" />
              {selectedDay && format(selectedDay, "d 'de' MMMM, yyyy", { locale: es })}
            </DialogTitle>
            <DialogDescription>Rentas programadas para este dia</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {dayRentas.map((renta) => (
              <div key={renta.id} className="p-3 border rounded-xl">
                <div className="flex items-start justify-between mb-1.5">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-allegra-gray">{renta.folio}</p>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getRentaColor(renta) }} />
                      <p className="font-medium text-sm text-allegra-dark truncate">{getRentaInflableNames(renta)}</p>
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${statusBadgeColors[renta.estatus]}`}>{renta.estatus}</Badge>
                </div>
                <div className="text-xs text-allegra-gray space-y-0.5">
                  <p>Horario: {renta.hora_inicio} - {renta.hora_fin}</p>
                  <p>Cliente: {renta.cliente_nombre || '-'}</p>
                  <div className="flex gap-3">
                    <span>Total: <strong className="text-allegra-dark">${renta.total}</strong></span>
                    {renta.saldo_pendiente > 0 && <span>Pendiente: <strong className="text-amber-600">${renta.saldo_pendiente}</strong></span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
